// src/pages/ManageUsers.jsx
import React, { useState, useEffect, useMemo } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import styles from './ManageUsers.module.css';
import { getUsers, updateUserPassword, updateUserStatus } from '../api/authApi';
import AppLayout from '../components/AppLayout';


// 1) Translation strings for English and Arabic
const translations = {
  en: {
    manageUsers: 'Manage Users',
    searchPlaceholder: 'Search by username...',
    type: 'Type:',
    status: 'Status:',
    active: 'Active',
    inactive: 'Inactive',
    activate: 'Activate',
    deactivate: 'Deactivate',
    changePassword: 'Change Password',
    confirmPasswordChange: 'Change Password',
    passwordFor: 'New password for',
    newPassword: 'New password',
    confirmPassword: 'Confirm password',
    savePassword: 'Save Password',
    passwordTooShort: 'Password must be at least 6 characters.',
    passwordMismatch: 'Passwords do not match.',
    noUsersFound: 'No users found.',
    errorTitle: 'Error',
    successTitle: 'Success',
    close: 'Close',
    confirmStatusChange: 'Confirm Status Change',
    confirmDeactivateQuestion: 'Are you sure you want to deactivate user',
    confirmActivateQuestion: 'Are you sure you want to activate user',
    yesDeactivate: 'Yes, Deactivate',
    yesActivate: 'Yes, Activate',
    cancel: 'Cancel',
    failedFetchUsers: 'Failed to fetch users. Please try again.',
    failedUpdateUser: 'Failed to update user status. Please try again.',
    failedUpdatePassword: 'Failed to update password. Please try again.',
    userStatusUpdated: 'User status updated successfully.',
    passwordUpdated: 'Password updated successfully.',
    homepage: 'Homepage',
  },
  ar: {
    manageUsers: 'إدارة المستخدمين',
    searchPlaceholder: 'ابحث باسم المستخدم...',
    type: 'النوع:',
    status: 'الحالة:',
    active: 'نشط',
    inactive: 'غير نشط',
    activate: 'تفعيل',
    deactivate: 'تعطيل',
    changePassword: 'تغيير كلمة المرور',
    confirmPasswordChange: 'تغيير كلمة المرور',
    passwordFor: 'كلمة المرور الجديدة للمستخدم',
    newPassword: 'كلمة المرور الجديدة',
    confirmPassword: 'تأكيد كلمة المرور',
    savePassword: 'حفظ كلمة المرور',
    passwordTooShort: 'كلمة المرور يجب ألا تقل عن 6 أحرف.',
    passwordMismatch: 'كلمتا المرور غير متطابقتين.',
    noUsersFound: 'لم يتم العثور على مستخدمين.',
    errorTitle: 'خطأ',
    successTitle: 'نجاح',
    close: 'إغلاق',
    confirmStatusChange: 'تأكيد تغيير الحالة',
    confirmDeactivateQuestion: 'هل أنت متأكد أنك تريد تعطيل المستخدم',
    confirmActivateQuestion: 'هل أنت متأكد أنك تريد تفعيل المستخدم',
    yesDeactivate: 'نعم، عطّل',
    yesActivate: 'نعم، فعّل',
    cancel: 'إلغاء',
    failedFetchUsers: 'فشل في جلب المستخدمين. حاول مرة أخرى.',
    failedUpdateUser: 'فشل في تحديث حالة المستخدم. حاول مرة أخرى.',
    failedUpdatePassword: 'فشل في تحديث كلمة المرور. حاول مرة أخرى.',
    userStatusUpdated: 'تم تحديث حالة المستخدم بنجاح.',
    passwordUpdated: 'تم تحديث كلمة المرور بنجاح.',
    homepage: 'الصفحة الرئيسية',
  },
};

const ManageUsers = () => {
  // 2) Add language state and translation helper
  const [language, setLanguage] = usePersistentLanguage();
  const t = (key) => translations[language][key];

  // 3) Toggle language function
  const toggleLanguage = () => {
    setLanguage((prevLang) => (prevLang === 'en' ? 'ar' : 'en'));
  };

  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmStatusChange, setConfirmStatusChange] = useState(null);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ password: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(true); // Loading state

  useEffect(() => {
    getUsers()
      .then((response) => {
        setUsers(response.data);
        setLoading(false); // Set loading to false after data is fetched
      })
      .catch((error) => {
        console.error('Error fetching users:', error);
        setErrorMessage(t('failedFetchUsers'));
        setLoading(false); // Set loading to false even if there's an error
      });
    // eslint-disable-next-line
  }, []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusClick = (user) => {
    setConfirmStatusChange({
      username: user.username,
      isActive: user.isActive !== false,
    });
  };

  const handlePasswordClick = (user) => {
    setPasswordTarget(user);
    setPasswordForm({ password: '', confirmPassword: '' });
    setPasswordError('');
  };

  
  const token = useMemo(() => localStorage.getItem('authToken'), []);
  const userInfo = useMemo(() => {
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          return {
            userType: payload.userType,
            requesterUsername: payload.username,
          };
        } catch (error) {
          console.error('Error parsing token payload:', error);
          return { userType: null, requesterUsername: null };
        }
      }
      return { userType: null, requesterUsername: null };
    }, [token]);
    
  const confirmUserStatusChange = async () => {
    if (!confirmStatusChange) return;
    const nextIsActive = !confirmStatusChange.isActive;
    try {
      setLoading(true); // Start loading before API call
      const response = await updateUserStatus(confirmStatusChange.username, nextIsActive);
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.username === confirmStatusChange.username ? response.data.user : user
        )
      );
      setSuccessMessage(`${t('userStatusUpdated')}: '${confirmStatusChange.username}'`);
      setConfirmStatusChange(null);
      setLoading(false); // Stop loading after successful status update
    } catch (error) {
      console.error('Error updating user status:', error);
      setErrorMessage(t('failedUpdateUser'));
      setLoading(false); // Stop loading even if there's an error
    }
  };

  const confirmPasswordChange = async () => {
    if (!passwordTarget) return;

    if (passwordForm.password.length < 6) {
      setPasswordError(t('passwordTooShort'));
      return;
    }

    if (passwordForm.password !== passwordForm.confirmPassword) {
      setPasswordError(t('passwordMismatch'));
      return;
    }

    try {
      setLoading(true);
      await updateUserPassword(passwordTarget.username, passwordForm.password);
      setSuccessMessage(`${t('passwordUpdated')}: '${passwordTarget.username}'`);
      setPasswordTarget(null);
      setPasswordForm({ password: '', confirmPassword: '' });
      setPasswordError('');
    } catch (error) {
      console.error('Error updating user password:', error);
      setPasswordError(error?.response?.data?.error || t('failedUpdatePassword'));
    } finally {
      setLoading(false);
    }
  };

  const visibleUsers = useMemo(() => {
    const narrowed = userInfo.userType === 'sales-manager'
      ? users.filter((user) => user.userType === 'sales' || user.userType === 'sales-manager')
      : users;

    return narrowed.filter((user) =>
      user.username.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [users, searchTerm, userInfo.userType]);

  const getUserTypeLabel = (type) => {
    const map = {
      admin: language === 'en' ? 'Admin' : 'مدير',
      manager: language === 'en' ? 'Factory Manager' : 'مشرف مصنع',
      'sales-manager': language === 'en' ? 'Sales Manager' : 'مدير المبيعات',
      accounting: language === 'en' ? 'Accounting' : 'المحاسبة',
      sales: language === 'en' ? 'Sales' : 'مبيعات',
      worker: language === 'en' ? 'Worker' : 'عامل',
    };
    return map[type] || type;
  };

  const canChangeUserStatus = (user) => {
    if (!['admin', 'manager', 'sales-manager'].includes(userInfo.userType)) {
      return false;
    }
    if (user.username === userInfo.requesterUsername) {
      return false;
    }
    if (userInfo.userType === 'sales-manager') {
      return user.userType === 'sales' || user.userType === 'sales-manager';
    }
    return true;
  };

  const canChangeUserPassword = (user) => {
    if (!['admin', 'manager', 'sales-manager'].includes(userInfo.userType)) {
      return false;
    }
    if (userInfo.userType === 'sales-manager') {
      return user.userType === 'sales' || user.userType === 'sales-manager';
    }
    return true;
  };

  return (
    <AppLayout
      title={t('manageUsers')}
      language={language}
      onToggleLanguage={toggleLanguage}
      userType={userInfo.userType}
      showHome
      homeLabel={t('homepage')}
      actions={
        !loading ? (
          <input
            type="text"
            className={styles.searchInput}
            placeholder={t('searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
          />
        ) : null
      }
      contentClassName={styles.manageUsersContainer}
      contentProps={{ 'aria-busy': loading }}
    >
      {/* Loading Indicator */}
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
        </div>
      )}

      {/* Main Content - Hidden when loading */}
      {!loading && (
        <>
          <div className={styles.userList}>
            {visibleUsers.length > 0 ? (
              visibleUsers.map((user) => (
                <div
                  key={user._id}
                  className={`${styles.userCard} ${
                    user.isActive === false ? styles.inactiveCard : ''
                  }`}
                >
                  <h2 className={styles.username}>{user.username}</h2>
                  <p className={styles.userType}>
                    <strong>{t('type')}</strong> {getUserTypeLabel(user.userType)}
                  </p>
                  <p className={styles.statusLine}>
                    <strong>{t('status')}</strong>
                    <span
                      className={`${styles.statusBadge} ${
                        user.isActive === false ? styles.statusInactive : styles.statusActive
                      }`}
                    >
                      {user.isActive === false ? t('inactive') : t('active')}
                    </span>
                  </p>
                  <div className={styles.cardActions}>
                    {canChangeUserStatus(user) && (
                      <button
                        className={
                          user.isActive === false
                            ? styles.activateButton
                            : styles.deactivateButton
                        }
                        onClick={() => handleStatusClick(user)}
                      >
                        {user.isActive === false ? t('activate') : t('deactivate')}
                      </button>
                    )}
                    {canChangeUserPassword(user) && (
                      <button
                        type="button"
                        className={styles.passwordButton}
                        onClick={() => handlePasswordClick(user)}
                      >
                        {t('changePassword')}
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className={styles.noUsers}>{t('noUsersFound')}</p>
            )}
          </div>

          {errorMessage && (
            <div className={styles.modalBackdrop}>
              <div className={styles.modalContent}>
                <h2>{t('errorTitle')}</h2>
                <p>{errorMessage}</p>
                <button
                  onClick={() => setErrorMessage(null)}
                  className={styles.modalButton}
                >
                  {t('close')}
                </button>
              </div>
            </div>
          )}

          {successMessage && (
            <div className={styles.modalBackdrop}>
              <div className={styles.modalContent}>
                <h2>{t('successTitle')}</h2>
                <p>{successMessage}</p>
                <button
                  onClick={() => setSuccessMessage(null)}
                  className={styles.modalButton}
                >
                  {t('close')}
                </button>
              </div>
            </div>
          )}

          {confirmStatusChange && (
            <div className={styles.modalBackdrop}>
              <div className={styles.modalContent}>
                <h2>{t('confirmStatusChange')}</h2>
                <p>
                  {confirmStatusChange.isActive
                    ? t('confirmDeactivateQuestion')
                    : t('confirmActivateQuestion')}{' '}
                  <strong>{confirmStatusChange.username}</strong>?
                </p>
                <div className={styles.modalButtonGroup}>
                  <button onClick={confirmUserStatusChange} className={styles.confirmButton}>
                    {confirmStatusChange.isActive ? t('yesDeactivate') : t('yesActivate')}
                  </button>
                  <button
                    onClick={() => setConfirmStatusChange(null)}
                    className={styles.cancelButton}
                  >
                    {t('cancel')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {passwordTarget && (
            <div className={styles.modalBackdrop}>
              <div className={styles.modalContent}>
                <h2>{t('confirmPasswordChange')}</h2>
                <p>
                  {t('passwordFor')} <strong>{passwordTarget.username}</strong>
                </p>
                {passwordError && <p className={styles.inlineError}>{passwordError}</p>}
                <div className={styles.formFields}>
                  <label>
                    <span>{t('newPassword')}</span>
                    <input
                      type="password"
                      value={passwordForm.password}
                      onChange={(event) =>
                        setPasswordForm((prev) => ({ ...prev, password: event.target.value }))
                      }
                      autoComplete="new-password"
                    />
                  </label>
                  <label>
                    <span>{t('confirmPassword')}</span>
                    <input
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(event) =>
                        setPasswordForm((prev) => ({ ...prev, confirmPassword: event.target.value }))
                      }
                      autoComplete="new-password"
                    />
                  </label>
                </div>
                <div className={styles.modalButtonGroup}>
                  <button onClick={confirmPasswordChange} className={styles.confirmButton}>
                    {t('savePassword')}
                  </button>
                  <button
                    onClick={() => {
                      setPasswordTarget(null);
                      setPasswordError('');
                    }}
                    className={styles.cancelButton}
                  >
                    {t('cancel')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </AppLayout>
  );
};

export default ManageUsers;
